import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SITE_URL } from "@/data/articlesPart1";
import { StylistStatusBadge } from "@/components/landing/StylistStatusBadge";
import { trackEvent } from "@/lib/analytics";
import { buildWhatsAppUrl } from "@/lib/utm";

const WhatsAppGlyph = ({ size = 22 }) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
  >
    <path d="M19.11 17.32c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.49 1.07 2.89 1.22 3.09c.15.2 2.1 3.21 5.08 4.5.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM16.03 26.34h-.01c-1.9 0-3.77-.51-5.4-1.48l-.39-.23-4.02 1.05 1.07-3.92-.25-.4c-1.06-1.69-1.62-3.64-1.62-5.65 0-5.85 4.76-10.6 10.62-10.6 2.83 0 5.5 1.1 7.5 3.11 2 2 3.11 4.67 3.11 7.5-.01 5.86-4.77 10.62-10.61 10.62zM27.06 4.94A15.42 15.42 0 0016.03 0C7.34 0 .28 7.06.28 15.75c0 2.78.73 5.49 2.11 7.87L.15 32l8.61-2.26a15.7 15.7 0 007.27 1.85h.01c8.68 0 15.75-7.06 15.75-15.75 0-4.21-1.64-8.16-4.73-11.13z" />
  </svg>
);

export default function Hello() {
  useSEO({
    title: "Angiolini — онлайн-подбор и примерка в бутике | WhatsApp",
    description:
      "Напишите нам в WhatsApp — подберём итальянскую верхнюю одежду онлайн или запишем на индивидуальную примерку в бутике в Алматы. Шубы, пальто, пуховики.",
    canonical: `${SITE_URL}/hello`,
  });

  const videoRef = useRef(null);
  const [mobile] = useState(
    () => window.matchMedia("(max-width: 767px)").matches
  );
  const [reduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    const v = videoRef.current;
    if (!v || reduced) return;
    v.muted = true;
    v.playsInline = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [reduced]);

  const bullets = [
    "Онлайн-подбор по фото и размерам",
    "Индивидуальная примерка в бутике",
    "Персональная консультация стилиста",
  ];

  return (
    <main
      data-testid="hello-page"
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: "var(--ink)" }}
    >
      {reduced ? (
        <img
          src={mobile ? "/media/poster-mobile.jpg" : "/media/poster.jpg"}
          alt="Angiolini — итальянская верхняя одежда"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <video
          ref={videoRef}
          data-testid="hello-video"
          className="absolute inset-0 w-full h-full object-cover"
          poster={mobile ? "/media/poster-mobile.jpg" : "/media/poster.jpg"}
          muted
          loop
          autoPlay
          playsInline
          preload="auto"
        >
          <source
            src={mobile ? "/media/hero-scrub-mobile.mp4" : "/media/hero-scrub.mp4"}
            type='video/mp4; codecs="avc1.640028"'
          />
          <source
            src={mobile ? "/media/hero-scrub-mobile.webm" : "/media/hero-scrub.webm"}
            type="video/webm"
          />
        </video>
      )}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(6,4,10,0.62) 0%, rgba(6,4,10,0.35) 40%, rgba(6,4,10,0.78) 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="pw-container pt-6 md:pt-8 flex items-center justify-between">
          <a
            href="/"
            data-testid="hello-logo"
            className="inline-flex items-center gap-2"
            aria-label="Angiolini"
          >
            <span className="text-white/90 font-serif italic text-xl md:text-2xl tracking-wide">
              Angiolini
            </span>
          </a>
          <a
            href="tel:+77018017755"
            data-testid="hello-phone"
            onClick={() => trackEvent("phone_click", { location: "hello_header" })}
            className="hidden sm:inline text-white/70 hover:text-white text-sm tracking-wide transition-colors"
          >
            +7 701 801 77 55
          </a>
        </header>

        <div className="pw-container flex-1 flex items-center py-14 md:py-20">
          <div className="max-w-2xl">
            <span className="pw-eyebrow !text-white/60 mb-5">
              Angiolini · Онлайн-консьерж
            </span>
            <h1
              className="text-white font-semibold"
              style={{
                fontSize: "clamp(2.1rem, 5.4vw, 4.6rem)",
                letterSpacing: "-0.045em",
                lineHeight: 1.04,
              }}
            >
              Подберём онлайн
              <br />
              или запишем на примерку
              <br />
              <span style={{ color: "var(--accent)" }}>в бутике.</span>
            </h1>

            <p className="mt-6 text-white/75 text-base md:text-lg max-w-xl leading-relaxed">
              Итальянские шубы, пальто и пуховики. Напишите нам в WhatsApp —
              стилист Angiolini подберёт изделие под вас и организует примерку в
              удобное время.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hello-whatsapp-cta"
                onClick={(e) => {
                  e.currentTarget.href = buildWhatsAppUrl();
                  trackEvent("whatsapp_click", { location: "hello" });
                }}
                className="group inline-flex items-center gap-3 rounded-full px-8 py-5 text-base md:text-lg font-medium text-white transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: "#25D366",
                  boxShadow:
                    "0 14px 40px rgba(37,211,102,0.45), 0 6px 14px rgba(0,0,0,0.35)",
                }}
              >
                <WhatsAppGlyph />
                <span>Написать в WhatsApp</span>
                <span
                  className="transition-transform duration-300 group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  →
                </span>
              </a>
              <a
                href="tel:+77018017755"
                data-testid="hello-call-cta"
                onClick={() => trackEvent("phone_click", { location: "hello" })}
                className="pw-btn pw-btn-ghost !text-white/85"
              >
                Позвонить
              </a>
            </div>

            <div className="mt-5">
              <StylistStatusBadge />
            </div>

            <ul className="mt-8 flex flex-col gap-3">
              {bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-center gap-3 text-white/80 text-sm md:text-[15px]"
                >
                  <Check
                    size={16}
                    strokeWidth={2.5}
                    style={{ color: "var(--accent)" }}
                    className="shrink-0"
                  />
                  {b}
                </li>
              ))}
            </ul>

            <p className="mt-10 text-white/50 text-[13px] max-w-md leading-relaxed">
              Бутик: Алматы, Нурлы Тау, ТЦ «Jurek Tau», этаж P1.
              <br />
              Отвечаем круглосуточно.
            </p>
          </div>
        </div>

        <footer className="pw-container pb-6 md:pb-8">
          <a
            href="/"
            data-testid="hello-full-site"
            className="text-white/50 hover:text-white/85 text-[13px] tracking-wide transition-colors"
          >
            ← Смотреть весь сайт Angiolini
          </a>
        </footer>
      </div>
    </main>
  );
}
